import {
  ArrowBack as ArrowBackIcon,
  InsertDriveFile as DocumentIcon,
  GetApp as DownloadIcon,
  Description as FileIcon,
  Folder as FolderIcon,
  Map as MapIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../contexts/ThemeContext";
import { api } from "../services/api";
import FileChecksumValue from "../components/common/FileChecksumValue";

// Tab panel component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`resources-tabpanel-${index}`}
      aria-labelledby={`resources-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Get icon based on file type
const getFileIcon = (fileType) => {
  if (fileType.includes("pdf")) {
    return <DocumentIcon />;
  } else if (fileType.includes("image")) {
    return <MapIcon />;
  } else if (
    fileType.includes("msword") ||
    fileType.includes("document") ||
    fileType.includes("text")
  ) {
    return <FileIcon />;
  } else {
    return <FileIcon />;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return bytes + " B";
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(1) + " KB";
  } else {
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }
};

// Get file name from path
const getFileName = (filePath) => {
  if (!filePath) return "";
  // Split by both forward and backward slashes to handle different path formats
  const parts = filePath.split(/[\\\/]/);
  return parts[parts.length - 1];
};

const ResourcesPage = () => {
  const navigate = useNavigate();
  const { themeMode } = useContext(ThemeContext);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = useTheme();

  // State for tab value
  const [tabValue, setTabValue] = useState(0);

  // State for selected category (0 = All)
  const [selectedCategory, setSelectedCategory] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch resources
      const resourcesResponse = await api.get("/resources");
      setResources(resourcesResponse.data);

      // Fetch categories
      const categoriesResponse = await api.get("/resources/categories");
      setCategories(categoriesResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load resources. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleDownload = (resourceId) => {
    // Navigate to the resource download page with the resourceId
    navigate(`/resources/download/${resourceId}`);
  };

  // Filter resources by category
  const getFilteredResources = () => {
    if (selectedCategory === 0) {
      return resources;
    } else if (selectedCategory === "null") {
      return resources.filter((resource) => !resource.category_id);
    }
    return resources.filter(
      (resource) => resource.category_id === selectedCategory,
    );
  };

  // Get resources by category for category view
  const getResourcesByCategory = () => {
    // Group by category
    const resourcesByCategory = {};

    // Add "Uncategorized" group
    resourcesByCategory["uncategorized"] = resources.filter(
      (resource) => !resource.category_id,
    );

    // Add each category group
    categories.forEach((category) => {
      resourcesByCategory[category.id] = resources.filter(
        (resource) => resource.category_id === category.id,
      );
    });

    return resourcesByCategory;
  };

  if (loading && resources.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Resources
      </Typography>

      <Paper elevation={3} sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="resource view tabs"
          centered
        >
          <Tab label="Category View" />
          <Tab label="List View" />
        </Tabs>

        {/* Category View */}
        <TabPanel value={tabValue} index={0}>
          {Object.keys(getResourcesByCategory()).length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center" }}>
              No resources available at this time.
            </Typography>
          ) : (
            <Box sx={{ px: 3, pb: 3 }}>
              {/* Uncategorized resources */}
              {getResourcesByCategory()["uncategorized"].length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Uncategorized
                  </Typography>
                  <Grid container spacing={3}>
                    {getResourcesByCategory()["uncategorized"].map(
                      (resource) => (
                        <Grid item xs={12} sm={6} md={4} key={resource.id}>
                          <Card
                            elevation={2}
                            sx={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              transition: "transform 0.2s",
                              background: "background.paper",
                              border: "1px solid",
                              boxShadow: "none",
                              borderColor: "divider",
                              "&:hover": {
                                transform: "translateY(-5px)",
                                boxShadow: themeMode === "dark" ? 8 : 6,
                              },
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                {getFileIcon(resource.file_type)}
                                <Typography
                                  variant="h6"
                                  component="h2"
                                  sx={{ ml: 1 }}
                                >
                                  {resource.name}
                                </Typography>
                              </Box>

                              {resource.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 1 }}
                                >
                                  {resource.description}
                                </Typography>
                              )}

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: "block" }}
                              >
                                Size: {formatFileSize(resource.file_size)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                File: {getFileName(resource.file_path)}
                              </Typography>
                              <FileChecksumValue file={resource} />
                            </CardContent>
                            <CardActions>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(resource.id)}
                              >
                                Download
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      ),
                    )}
                  </Grid>
                </Box>
              )}

              {/* Categorized resources */}
              {categories.map((category) => {
                const categoryResources = getResourcesByCategory()[category.id];

                if (categoryResources.length === 0) {
                  return null;
                }

                return (
                  <Box sx={{ mb: 4 }} key={category.id}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {category.name}
                    </Typography>

                    {category.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {category.description}
                      </Typography>
                    )}

                    <Grid container spacing={3}>
                      {categoryResources.map((resource) => (
                        <Grid item xs={12} sm={6} md={4} key={resource.id}>
                          <Card
                            elevation={2}
                            sx={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              transition: "transform 0.2s",
                              background: "background.paper",
                              border: "1px solid",
                              boxShadow: "none",
                              borderColor: "divider",
                              "&:hover": {
                                transform: "translateY(-5px)",
                                boxShadow: themeMode === "dark" ? 8 : 6,
                              },
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                {getFileIcon(resource.file_type)}
                                <Typography
                                  variant="h6"
                                  component="h2"
                                  sx={{ ml: 1 }}
                                >
                                  {resource.name}
                                </Typography>
                              </Box>

                              {resource.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 1 }}
                                >
                                  {resource.description}
                                </Typography>
                              )}

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: "block" }}
                              >
                                Size: {formatFileSize(resource.file_size)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                File: {getFileName(resource.file_path)}
                              </Typography>
                              <FileChecksumValue file={resource} />
                            </CardContent>
                            <CardActions>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(resource.id)}
                              >
                                Download
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </TabPanel>

        {/* List View */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={2}>
              {/* Category sidebar */}
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Categories
                  </Typography>
                  <List component="nav" sx={{ width: "100%" }}>
                    <ListItem
                      button
                      selected={selectedCategory === 0}
                      sx={{
                        background:
                          selectedCategory === 0
                            ? theme.palette.action.hover
                            : "transparent",
                      }}
                      onClick={() => handleCategorySelect(0)}
                    >
                      <ListItemIcon>
                        <FolderIcon />
                      </ListItemIcon>
                      <ListItemText primary="All Resources" />
                      <Chip
                        label={resources.length}
                        size="small"
                        color={selectedCategory === 0 ? "primary" : "default"}
                      />
                    </ListItem>

                    {/* Uncategorized option */}
                    <ListItem
                      button
                      selected={selectedCategory === "null"}
                      sx={{
                        background:
                          selectedCategory === "null"
                            ? theme.palette.action.hover
                            : "transparent",
                      }}
                      onClick={() => handleCategorySelect("null")}
                    >
                      <ListItemIcon>
                        <FolderIcon />
                      </ListItemIcon>
                      <ListItemText primary="Uncategorized" />
                      <Chip
                        label={resources.filter((r) => !r.category_id).length}
                        size="small"
                        color={
                          selectedCategory === "null" ? "primary" : "default"
                        }
                      />
                    </ListItem>

                    <Divider sx={{ my: 1 }} />

                    {/* Category list */}
                    {categories.map((category) => {
                      const count = resources.filter(
                        (r) => r.category_id === category.id,
                      ).length;
                      if (count === 0) return null;

                      return (
                        <ListItem
                          button
                          key={category.id}
                          selected={selectedCategory === category.id}
                          sx={{
                            background:
                              selectedCategory === category.id
                                ? theme.palette.action.hover
                                : "transparent",
                          }}
                          onClick={() => handleCategorySelect(category.id)}
                        >
                          <ListItemIcon>
                            <FolderIcon />
                          </ListItemIcon>
                          <ListItemText primary={category.name} />
                          <Chip
                            label={count}
                            size="small"
                            color={
                              selectedCategory === category.id
                                ? "primary"
                                : "default"
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Grid>

              {/* Resources list */}
              <Grid item xs={12} md={9}>
                <Box elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {selectedCategory === 0
                      ? "All Resources"
                      : selectedCategory === "null"
                        ? "Uncategorized"
                        : categories.find((c) => c.id === selectedCategory)
                            ?.name || "Resources"}
                  </Typography>

                  {getFilteredResources().length === 0 ? (
                    <Typography sx={{ p: 2, textAlign: "center" }}>
                      No resources available in this category.
                    </Typography>
                  ) : (
                    <List>
                      {getFilteredResources().map((resource) => (
                        <ListItem
                          key={resource.id}
                          sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <ListItemIcon>
                            {getFileIcon(resource.file_type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={resource.name}
                            secondary={
                              <>
                                {resource.description && (
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    display="block"
                                    sx={{ mb: 1 }}
                                  >
                                    {resource.description}
                                  </Typography>
                                )}
                                <Typography variant="caption" component="span">
                                  Size: {formatFileSize(resource.file_size)} •
                                  File: {getFileName(resource.file_path)}
                                  {resource.category && (
                                    <Chip
                                      label={resource.category.name}
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Typography>
                                <FileChecksumValue file={resource} />
                              </>
                            }
                          />
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(resource.id)}
                          >
                            Download
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default ResourcesPage;
